import React, {useDeferredValue, useEffect,useMemo,useRef,useState} from "react";

import { GoSearch } from "react-icons/go";
import { FiChevronLeft, FiChevronRight, FiChevronUp } from "react-icons/fi";
import { TfiClose } from "react-icons/tfi";
import { flushSync } from "react-dom";
import ContainerResultSearch from "./components/ResultMultiSearchPopup";
import {CurrentCity,getLockedFilterValues,getMultiSearchResults, normalizeCitySearch, PresentSearchFilters, topCities, useSwipeDownToClose, VilleSuggestion} from "./hookSearchPage";
import { useClickOutside } from "../../../components/hook/clickOutside";
import { IoMdPin } from "react-icons/io";
import { MdRestartAlt } from "react-icons/md";
import { allShowFilter, FilterValue, formatFilterLabel, getCuisineDisplayLabel, listFilters, slugify, TIME_FILTERS } from "../../../components/types/types";
import axios from "axios";
import { SearchMiniRestoIndexed } from "../../../context/RestaurantContext";
import { FaSliders } from "react-icons/fa6";
import HookFilter from "./components/hookFilterSearch";

interface HookMultiSearchMobileProps {
    showMultiSearch: boolean;
    setShowMultiSearch: React.Dispatch<React.SetStateAction<boolean>>;

    suggestions: any[];

    miniRestos: any[];

    refCloseSearch: React.RefObject<HTMLDivElement>;
}

export const HookMultiSearchMobile: React.FC<HookMultiSearchMobileProps> = React.memo(({showMultiSearch,setShowMultiSearch,suggestions,miniRestos,refCloseSearch}) => {
    const [multiSearchValue, setMultiSearchValue] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isMultiSearchActive, setIsMultiSearchActive] = useState(false);
    const refMultiSearchResult = useRef<HTMLDivElement>(null);
    const refContainerMultiSearch = useRef<HTMLDivElement>(null);
    const refInputMultiSearch = useRef<HTMLInputElement>(null);


    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setDebouncedSearch(multiSearchValue);
        }, 120);
        return () => {
            window.clearTimeout(timeout);
        };

    }, [multiSearchValue]);


    useClickOutside(refContainerMultiSearch,showMultiSearch,() => {
            setShowMultiSearch(false);
    }, refCloseSearch);

    useEffect(() => {
        if (!showMultiSearch) return;
        refInputMultiSearch.current?.focus();
    }, [showMultiSearch]);
    useEffect(() => {
        if (showMultiSearch) return;

        setMultiSearchValue("");
        setDebouncedSearch("");
        setIsMultiSearchActive(false);

    }, [showMultiSearch]);


    const multiSearchResults = useMemo(() => {
        if (!isMultiSearchActive)  return [];
        const search = debouncedSearch.trim();
        if (search.length < 1) return [];
        return getMultiSearchResults({
            search,
            miniRestos
        });
    }, [isMultiSearchActive,debouncedSearch, miniRestos]);


    return (
        <>
            <div className="containerIconSearch flexAllCenter primaryBack" onClick={() => setShowMultiSearch(prev => !prev)}>{showMultiSearch ? <FiChevronLeft className="iconSearch iconChevronSearch" size={23}/> : <GoSearch className="iconSearch" size={20}/>}</div>
            {showMultiSearch && 
                <div className='containerMultiSearch primaryBack' ref={refContainerMultiSearch}>
                    <div className="containerInput primaryBack">
                        <GoSearch className="iconSearch" size={20}/>
                        <input type="text" ref={refInputMultiSearch} className="XsmallText" placeholder="Rechercher un restaurant..." value={multiSearchValue} onChange={((e) => {setMultiSearchValue(e.target.value);setIsMultiSearchActive(true)})}  enterKeyHint="search" name="search-filter-restaurant" autoComplete="new-password"/>
                        {multiSearchValue && <TfiClose className="closeInput" onClick={() => {setMultiSearchValue("");setDebouncedSearch("");setIsMultiSearchActive(false);refInputMultiSearch.current?.focus();}}/>}
                    </div>
                    <div className={`containerResult smallText ${isMultiSearchActive ? 'isOpen' : ''}`}  ref={refMultiSearchResult}>
                        <div className="shadowTop"></div>
                        <div className="shadowBottom"></div>
                        {multiSearchValue ? <ContainerResultSearch multiSearchResults={multiSearchResults} />
                        : <>{suggestions && suggestions.length > 0 ?  <ContainerResultSearch multiSearchResults={suggestions} /> :  <div className="listSuggest"><span className="XsmallText" style={{fontStyle:'italic',color:'#737373'}}>Aucune suggestions</span></div>}</>
                        }
                    </div>
                </div>}
        </>
    );
});


const PROXIMITY_CITY: NonNullable<CurrentCity> = {
    name: "À proximité",
    slug: "restaurants-a-proximite",
};

interface HookCitySearchMobileProps {
    partSelect: string;
    setPartSelect: React.Dispatch<React.SetStateAction<string>>;
    currentCity: CurrentCity | null;
    setCurrentCity: React.Dispatch<React.SetStateAction<CurrentCity | null>>;

    miniRestos: any[];
    handleSuggestionCity: (city: CurrentCity) => void;
    setImpossibleCity: React.Dispatch<React.SetStateAction<boolean>>;
    openSearchPage?:boolean,
}

export const HookCitySearchMobile: React.FC<HookCitySearchMobileProps> =React.memo(({partSelect,setPartSelect,currentCity,setCurrentCity,miniRestos,handleSuggestionCity,setImpossibleCity,openSearchPage=true}) => {
    const [suggestionsCity, setSuggestionsCity] =useState<VilleSuggestion[]>([]);
    const [currentSearchCity, setCurrentSearchCity] =useState("");
    const [isCitySearchActive, setIsCitySearchActive] =useState(false);
    const refSuggestList = useRef<HTMLDivElement>(null);

    const showTopCities = currentSearchCity.trim().length < 1 || !isCitySearchActive;

    const visibleCityLabel = useMemo(() => {
        if (!currentCity) return {base:"Où voulez-vous manger ?"};
        const isProximity = currentCity.slug === "restaurants-a-proximite";
        if (isProximity) return {base:"À proximité"};
        return {base:currentCity.name};
    }, [currentCity]);


    const cityListToDisplay = useMemo(() => {
        if(!showTopCities ) return suggestionsCity.slice(0, 5).map(city => ({ name: city.name, slug: slugify(city.name),latitude: String(city.latitude),longitude: String(city.longitude),cityNameRaw: city.name, }))
        if (!currentCity) return topCities;
        const normalizedCurrentCity = normalizeCitySearch(currentCity.name);
        return topCities.filter( city => normalizeCitySearch(city.name) !== normalizedCurrentCity);
    }, [showTopCities, suggestionsCity,currentCity]);
    const cityCountMap = useMemo(() => {
        const map = new Map<string, number>();
        for (const resto of miniRestos) {
            const addressCity = resto._addressCityNorm;
            const visibleCity = resto._visibleCityNorm;
            if (addressCity) map.set(addressCity,(map.get(addressCity) ?? 0) + 1);
            if (visibleCity && visibleCity !== addressCity) map.set(visibleCity,(map.get(visibleCity) ?? 0) + 1);
        }
        return map;
    }, [miniRestos]);
    const cityListWithCount = useMemo(() => {
        const cities = cityListToDisplay.map((city) => ({
            ...city,
            restaurantCount:
                city.name === "À proximité"
                    ? null
                    : cityCountMap.get(normalizeCitySearch(city.name)) ?? 0
        }));
        return  cities.sort((a, b) => (b.restaurantCount ?? 0) - (a.restaurantCount ?? 0));
    }, [cityListToDisplay, cityCountMap, showTopCities]);

    useEffect(() => {
        const search = currentSearchCity.trim();
        if (partSelect !== "city") return;
        if (search.length < 1) {
            setSuggestionsCity([]);
            return;
        }
        const controller = new AbortController();
        const timeout = window.setTimeout(async () => {
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_DIRECTION_REQUEST}/api/getVille`,
                    { name: search },
                    { signal: controller.signal }
                );

                if (controller.signal.aborted) return;

                setSuggestionsCity(response.data || []);
            } catch (error) {
                if (axios.isCancel(error) ||controller.signal.aborted) return;
                console.error("Erreur lors de la recherche des villes:",error);
                setSuggestionsCity([]);
            }
        }, 250);
        return () => {
            window.clearTimeout(timeout);
            controller.abort();
        };
    }, [currentSearchCity, partSelect]);

    useEffect(() => {
        if (partSelect === "city") return;

        setCurrentSearchCity("");
        setSuggestionsCity([]);
        setIsCitySearchActive(false);
    }, [partSelect]);
    useEffect(() => {
        if(openSearchPage) {
            requestAnimationFrame(() => {
                refSuggestList.current?.scrollTo({
                    top: 0,
                    behavior: "auto"
                });
            });
        }
    },[openSearchPage])

    useEffect(() => {
        if (currentCity) return;
        setCurrentSearchCity("");
    }, [currentCity]);


    const handlePartCity = () => {
        if (partSelect === "city") {
            setCurrentSearchCity("");
            return;
        }
        setIsCitySearchActive(false);
        setPartSelect("city");
        setCurrentSearchCity("");
    };


    return (
        <div className={`elemContent cityPart ${partSelect === 'city' ? 'isOpen' : ''}`} >
            <div className="basePart" onClick={handlePartCity}>
                <IoMdPin size={15} className="icon"/>
                <div className="basePartTextWrapper">
                    <span className={`textBase smallText`}>{visibleCityLabel.base} </span>
                </div>
                {partSelect === 'city' && <span className={`textReinit XsmallText pointer flexAllCenter ${currentCity ? 'isActive' : ''}`}  onClick={() => setCurrentCity(null)}>
                    <MdRestartAlt size={16} className="icon"/>
                    Réinitialiser
                </span>}
                {(currentCity && partSelect !== 'city' ) && <TfiClose className="iconReset" size={16} onClick={(e) => {e.stopPropagation();setCurrentCity(null)}} />}
            </div>
            {partSelect === 'city' && <>
            <div className="containerElemInput">
                <GoSearch className="iconSearch" size={20}/>
                <input
                    className='smallText'
                    placeholder="Rechercher une ville..."
                    value={currentSearchCity}
                    enterKeyHint="search"
                    name="search-city"
                    autoComplete="new-password"
                    onChange={(e) => {setCurrentSearchCity(e.target.value);setIsCitySearchActive(true)}} />
                {currentSearchCity && <TfiClose className="close" size={17} onClick={(e) => {e.stopPropagation();setCurrentSearchCity("")}}/>}
            </div>
            <div className="suggestListWrapper">
                <div className="shadowTop"></div>
                <div className="shadowBottom"></div>
                <div className={`suggestList XsmallText `} ref={refSuggestList}>
                    {(showTopCities && currentCity?.slug !== "restaurants-a-proximite") &&  <>
                            <div className="elemSuggestion pointer lowOpacityHover"  onClick={() => handleSuggestionCity(PROXIMITY_CITY)}>
                                <span>
                                    {"À proximité"}
                                </span>
                                <div className="containerIcon flexAllCenter"><FiChevronRight size={15} className="icon"/></div>
                            </div>
                            <div className="separationList"></div>
                    </>}
                    {cityListWithCount && cityListWithCount.length > 0 ? cityListWithCount.map((elem,id) => {
                        return <React.Fragment key={'suggestCity'+elem.slug}>
                            <div className={`elemSuggestion pointer lowOpacityHover ${(elem.restaurantCount !== null && elem.restaurantCount === 0) ? 'isLock' : ''}`}  onClick={() => {if(elem.restaurantCount !== null && elem.restaurantCount === 0){setImpossibleCity(true)}else{handleSuggestionCity(elem)}}}>
                                {!showTopCities && <GoSearch className="iconSearch" size={11} />}
                                <span>
                                {elem.name}
                                {elem.restaurantCount !== null && (
                                    <span className="citySuggestionCount">
                                        {`(${elem.restaurantCount})`}
                                    </span>
                                )}</span>
                                <div className="containerIcon flexAllCenter"><FiChevronRight size={15} className="icon"/></div>
                            </div>
                            {id < cityListWithCount.length-1 && <div className="separationList"></div>}
                        </React.Fragment>
                    })
                    : <span className="XsmallText noMatchResult">Aucune ville correspondante dans le Maine-et-Loire.</span>}
                </div>
            </div>
            </>}
        </div>
    );
});


interface HookFilterMobileProps {
    partSelect: string;
    setPartSelect: React.Dispatch<React.SetStateAction<string>>;
    currentFilter: FilterValue[];
    visibleCurrentFilters: FilterValue[];
    setCurrentFilter: React.Dispatch<React.SetStateAction<FilterValue[]>>;

    presentSearchFilters: PresentSearchFilters;
    contextualMiniRestos: SearchMiniRestoIndexed[];
    miniRestosLoaded: boolean;
    visibleFilterSummary: string;

    addFilter: (value: FilterValue) => void;
    applyFilterGroup: (groupValues: FilterValue[],selectedValues: FilterValue[]) => void;

}

export const HookFilterMobile: React.FC<HookFilterMobileProps> =React.memo(({partSelect,setPartSelect,currentFilter,visibleCurrentFilters,setCurrentFilter,presentSearchFilters,contextualMiniRestos,miniRestosLoaded,visibleFilterSummary,addFilter,applyFilterGroup}) => {

    const [currentSearchFilter, setCurrentSearchFilter] = useState("");
    const deferredSearchFilter = useDeferredValue(currentSearchFilter);
    const [isFilterSelectExpanded, setIsFilterSelectExpanded] = useState(false);
    const [showFilterBar, setShowFilterBar] = useState(false);
    const refContainerFilter = useRef<HTMLDivElement | null>(null);
    const refInputBar = useRef<HTMLInputElement | null>(null);

    const visibleSelectedFilters = isFilterSelectExpanded ? visibleCurrentFilters : visibleCurrentFilters.slice(0, 2);
    const hiddenSelectedFiltersCount = visibleCurrentFilters.length - visibleSelectedFilters.length;

    const closeFilterBar = () => {
        refInputBar.current?.blur();

        setShowFilterBar(false);
        setCurrentSearchFilter("");

        window.setTimeout(() => {
            const container = refContainerFilter.current;
            if (!container) return;

            container.scrollTop = 0;
            container.scrollLeft = 0;

            container.style.transform = "";
            container.style.transition = "";
            container.style.willChange = "";
        }, 150);
    };
    const {dragHandlers} = useSwipeDownToClose({
        containerRef:refContainerFilter,
        onClose: closeFilterBar,
        isForFullBar:true
    });

    useEffect(() => {
        if (showFilterBar) return;

        setCurrentSearchFilter("");
    }, [showFilterBar]);


    useEffect(() => {
        if (partSelect === "filter") return;

        setCurrentSearchFilter("");
        setIsFilterSelectExpanded(false);
        setShowFilterBar(false);
    }, [partSelect]);


    const currentListFilter = useMemo(() => {
        const filterSet = new Set(presentSearchFilters.filters);
        return listFilters.map((elem) => {
            if (elem.value === "Type de restaurant") return {...elem,liste: presentSearchFilters.types};
            if (elem.value === "Origine de la cuisine") return {...elem,liste: presentSearchFilters.cuisines};
            if (elem.value === "Notes") return elem;
            return {...elem,liste: elem.liste?.filter((x) => filterSet.has(x)) ?? [],};
        });
    }, [presentSearchFilters]);
    const displayedFilters = useMemo(() => {
        return (currentListFilter || []).filter((f) => {
            return (f.liste?.length || allShowFilter.includes(f.value));
        });
    }, [currentListFilter]);
    const candidateFilterValues = useMemo(() => {
        const values: string[] = [];
        for (const filter of displayedFilters) {
            if (filter.value === "Prix par personne") continue;
            for (const value of filter.liste ?? []) {
                if (typeof value === "string")  values.push(value);
            }
        }
        return values;
    }, [displayedFilters]);
    const lockedFilterValues = useMemo(() => {
        if (partSelect !== "filter" ||!miniRestosLoaded ) return new Set<string>();
        return getLockedFilterValues(contextualMiniRestos,currentFilter,presentSearchFilters,candidateFilterValues);
    }, [partSelect,miniRestosLoaded,contextualMiniRestos,currentFilter,presentSearchFilters,candidateFilterValues]);
    const typeResultCounts = useMemo(() => {
        const counts = new Map<string, number>();
        for (const resto of contextualMiniRestos) {
            const types = resto.type ?? [];
            for (const type of types) {
                counts.set(type,(counts.get(type) ?? 0) + 1);
            }
        }
        return counts;
    }, [contextualMiniRestos]);
    const handlePartFilter = () => {
        if (partSelect === "filter") return;
        setPartSelect("filter");
    };
    const clearVisibleFilters = () => {
        setCurrentFilter(prev =>
            prev.filter(filter => typeof filter === "string" && TIME_FILTERS.has(filter))
        );
    };
    const handleFilterInputPointerDown = (e: React.PointerEvent<HTMLInputElement>) => {
        if (showFilterBar) return;
        e.preventDefault();
        flushSync(() => {
            setShowFilterBar(true);
        });

        refInputBar.current?.focus({
            preventScroll: true,
        });
    };
    
    return (
        <div className={`elemContent filterPart ${partSelect === 'filter' ? 'isOpen' : ''}`} >
            <div className="basePart" onClick={handlePartFilter}>
                <FaSliders  size={15} className="icon iconSmall"/>
                <div className="basePartTextWrapper">
                    <span className={`textBase smallText`}>
                        Filtres {partSelect !== 'filter' && <span className={`explainFilter ${visibleCurrentFilters.length > 0 ? 'isActive' : ''}`}>{visibleFilterSummary}</span>}
                    </span>
                </div>
                
                {partSelect === 'filter' && <span className={`textReinit XsmallText pointer flexAllCenter ${visibleCurrentFilters.length > 0 ? 'isActive' : ''}`}  onClick={clearVisibleFilters}>
                    <MdRestartAlt size={17} className="icon"/>
                    Réinitialiser
                </span>}
                {(visibleCurrentFilters.length > 0 && partSelect !== 'filter' ) && <TfiClose className="iconReset" size={16} onClick={(e) => {e.stopPropagation();clearVisibleFilters()}} />}
            </div>
            {partSelect === "filter" && (
                <>
                        <div className={`shadowBottomListFilter ${showFilterBar ? 'isFull' : ''}`}></div>
                        {showFilterBar && <div className="maskContainerShowAllFilter"></div>}
                        <div className={`listAllFilter ${showFilterBar ? 'isFull' : ''}`} ref={refContainerFilter}>
                            
                            {showFilterBar && <div className="topPartFull primaryBack" {...dragHandlers}>
                                <span className="midTextBold">Filtres</span>
                                <FiChevronUp  onPointerDown={(e) => {e.stopPropagation(); }} onClick={(e) => {e.stopPropagation();closeFilterBar(); }} size={23}/>
                            </div>}
                            <div className="containerElemInput" style={currentSearchFilter ? {marginBottom:'10px'} : {}}>
                                <GoSearch className="iconSearch" size={20}/>
                                <input
                                    className='smallText'
                                    placeholder="Rechercher un filtre..."
                                    value={currentSearchFilter}
                                    ref={refInputBar}
                                     onPointerDown={handleFilterInputPointerDown}
                                    onChange={(e) => {setCurrentSearchFilter(e.target.value)}} />
                                {currentSearchFilter && <TfiClose className="close" size={17} onClick={() => setCurrentSearchFilter("")}/>}
                            </div>
                            {!currentSearchFilter && <div className={`listFilterSelect XsmallText ${isFilterSelectExpanded ? 'isExpanded' : 'notExpanded'} ${visibleCurrentFilters.length > 0 ? 'isOpen' : ''}`}>
                                {visibleSelectedFilters.map((filter, index) => (
                                    <div key={'elemSearch' + index} onClick={() => setCurrentFilter(currentFilter.filter(elem => elem !== filter))} className="elem"> {getCuisineDisplayLabel(formatFilterLabel(filter))} </div>
                                ))}
                                {!isFilterSelectExpanded && hiddenSelectedFiltersCount > 0 && (
                                    <div className="overflowBubble" onClick={() => setIsFilterSelectExpanded(true)}> +{hiddenSelectedFiltersCount}</div>
                                )}

                                {isFilterSelectExpanded && visibleCurrentFilters.length > 2 && (
                                    <div className="overflowBubble" onClick={() => setIsFilterSelectExpanded(false)} >Voir moins <FiChevronUp size={12} /></div>
                                )}  
                            </div>}
                            {displayedFilters && displayedFilters.map((filter, index) => {
                                return <React.Fragment key={filter.value} >
                                    <HookFilter  filterResultCounts={filter.value === "Type de restaurant" ? typeResultCounts : undefined} lockedFilterValues={lockedFilterValues} searchValue={deferredSearchFilter} filter={filter} listFilter={currentFilter} addFilter={addFilter} applyFilterGroup={applyFilterGroup}/>
                                    {index < displayedFilters.length-1 && !currentSearchFilter && <div className="sepElemFilter"></div>}
                                </React.Fragment>
                            })}  
                        </div>
                </>
            )}
        </div>
    )
})